// Reporting's Download dialog. EXCEL ONLY (owner, 2026-11-04 — D-472: "let's
// skip the PDF offering and just assume Excel"): every download is one
// workbook, the Table view's rows on one sheet and the Invoices view's rows on
// the other. The dialog asks one thing, in a plain sentence: whether the
// invoices' own PDFs come too. Unchecked each time it opens.
import { useEffect, useState } from 'react'
import {
  Body1,
  Button,
  Caption1,
  Checkbox,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  ProgressBar,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { AppDialog } from './AppDialog'
import { FrozenWhileClosed } from './FrozenWhileClosed'
import { useDialogFormStyles } from './dialogForm'
import { ErrorBanner } from './States'

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  hint: { color: tokens.colorNeutralForeground3, display: 'block', marginInlineStart: '32px' },
})

interface Props {
  open: boolean
  chartTitle: string
  /** The listing's count, to refuse the PDFs past the cap. `null` while unknown. */
  invoiceCount: number | null
  /** The most invoices a report with documents may cover. */
  invoiceCap: number
  busy: boolean
  /** What the export is doing now, while `busy`. */
  progressLabel: string | null
  progress: number | null
  error: string | null
  onDownload: (includeInvoices: boolean) => void
  onClose: () => void
}

export function ReportExportDialog({
  open,
  chartTitle,
  invoiceCount,
  invoiceCap,
  busy,
  progressLabel,
  progress,
  error,
  onDownload,
  onClose,
}: Props) {
  const s = useStyles()
  const f = useDialogFormStyles()
  const [includeInvoices, setIncludeInvoices] = useState(false)
  useEffect(() => {
    if (open) setIncludeInvoices(false)
  }, [open])

  const overCap = invoiceCount !== null && invoiceCount > invoiceCap

  return (
    <AppDialog open={open} onOpenChange={(_, d) => !d.open && !busy && onClose()}>
      <DialogSurface>
        <FrozenWhileClosed open={open}>
          <DialogBody>
            <DialogTitle>Download {chartTitle}</DialogTitle>
            <DialogContent className={f.beforeActions}>
              <div className={s.body}>
                <Body1>Would you like the invoices&rsquo; PDFs as well?</Body1>
                <div>
                  <Checkbox
                    checked={includeInvoices && !overCap}
                    disabled={busy || overCap}
                    onChange={(_, d) => setIncludeInvoices(d.checked === true)}
                    label="Include invoices"
                  />
                  {overCap && (
                    <Caption1 className={s.hint}>
                      {`These filters match ${invoiceCount} invoices, and PDFs can be included for at most ${invoiceCap}. Narrow the filters to include them.`}
                    </Caption1>
                  )}
                </div>
                {busy && progressLabel !== null && (
                  <div>
                    <Body1>{progressLabel}</Body1>
                    {progress !== null && <ProgressBar value={progress} thickness="large" />}
                  </div>
                )}
                <ErrorBanner message={error} />
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button appearance="primary" disabled={busy} onClick={() => onDownload(includeInvoices && !overCap)}>
                {busy ? 'Downloading…' : 'Download'}
              </Button>
            </DialogActions>
          </DialogBody>
        </FrozenWhileClosed>
      </DialogSurface>
    </AppDialog>
  )
}
